import { ApiConfig } from '@/config/api.config';
import axios from 'axios';
import type { UseQueryOptions } from '@tanstack/react-query';
import { HEALTH_CHECK_INTERVAL_MS } from './constants';

export type MaintenanceStatus = {
    enabled: boolean;
    allowedLogins: string[];
};

type MaintenanceStatusResponse = {
    enabled?: unknown;
    allowedLogins?: unknown;
};

export const maintenanceStatusQueryKey = [
    'maintenance-status',
    ApiConfig.maintenanceStatusUrl ?? 'maintenance-status-url-missing',
] as const;

export const isMaintenanceModeActive = (status: MaintenanceStatus | undefined): boolean =>
    status?.enabled === true;

export const hasMaintenanceLoginAccess = (status: MaintenanceStatus | undefined): boolean =>
    isMaintenanceModeActive(status) && (status?.allowedLogins.length ?? 0) > 0;

export const isLoginAllowedDuringMaintenance = (
    email: string,
    allowedLogins: string[],
): boolean =>
    allowedLogins.some((login) => login.toLowerCase() === email.toLowerCase());

const parseMaintenanceStatus = (data: unknown): MaintenanceStatus => {
    if (!data || typeof data !== 'object') {
        return { enabled: false, allowedLogins: [] };
    }

    const response = data as MaintenanceStatusResponse;
    const allowedLogins = Array.isArray(response.allowedLogins)
        ? response.allowedLogins.filter((item): item is string => typeof item === 'string')
        : [];

    return {
        enabled: response.enabled === true,
        allowedLogins,
    };
};

export const fetchMaintenanceStatus = async (): Promise<MaintenanceStatus> => {
    const maintenanceStatusUrl = ApiConfig.maintenanceStatusUrl;

    if (!maintenanceStatusUrl) {
        throw new Error('Maintenance status URL is not configured');
    }

    try {
        const response = await axios.get(maintenanceStatusUrl, {
            validateStatus: () => true,
            timeout: 10_000,
        });

        if (response.status !== 200) {
            return { enabled: false, allowedLogins: [] };
        }

        return parseMaintenanceStatus(response.data);
    } catch {
        return { enabled: false, allowedLogins: [] };
    }
};

export const hasMaintenanceStatusUrl = () => Boolean(ApiConfig.maintenanceStatusUrl);

export const getMaintenanceStatusQueryOptions = (options?: {
    enabled?: boolean;
    refetchInterval?: number;
}): UseQueryOptions<
    MaintenanceStatus,
    Error,
    MaintenanceStatus,
    typeof maintenanceStatusQueryKey
> => {
    const enabled = (options?.enabled ?? true) && hasMaintenanceStatusUrl();
    const refetchInterval = enabled
        ? options?.refetchInterval ?? HEALTH_CHECK_INTERVAL_MS
        : false;

    return {
        queryKey: maintenanceStatusQueryKey,
        queryFn: fetchMaintenanceStatus,
        enabled,
        retry: false,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        refetchInterval,
        refetchIntervalInBackground: true,
    };
};
